<template>
  <section class="monitor-section" aria-labelledby="metrics-heading">
    <header class="section-heading">
      <div>
        <h2 id="metrics-heading">运行数值</h2>
        <p>
          连接与任务数来自同一份采样；点击 Agent／创意任务数查看对应记录。未取得数值时显示「—」。
        </p>
      </div>
    </header>
    <div v-for="group in groups" :key="group.label" class="metric-row">
      <h3>{{ group.label }}</h3>
      <dl>
        <div v-for="item in group.items" :key="item.label">
          <dt>{{ item.label }}</dt>
          <dd>
            <button
              v-if="item.target && item.value !== null"
              class="text-action metric-action"
              :aria-label="`查看${group.label}${item.label}的 ${item.value} 条记录`"
              @click="showTasks(item.target)"
            >
              {{ item.value }}
            </button>
            <span v-else>{{ item.value ?? '—' }}</span>
          </dd>
        </div>
      </dl>
    </div>
  </section>
</template>
<script setup lang="ts">
import { computed } from 'vue';
import type { MonitorTaskKind } from 'shared';
import { useAdminRuntimeMonitorStore } from '@/stores/admin-runtime-monitor.store';

const store = useAdminRuntimeMonitorStore();
type Target = { kind: MonitorTaskKind; status: string; scope: 'active' | 'errors' };
type Metric = { label: string; value: number | null; target?: Target };
function value(id: string, key: string): number | null {
  const item = store.overview?.services.find((row) => row.id === id)?.metrics[key];
  return typeof item === 'number' ? item : null;
}
function queueTotal(key: string): number | null {
  const queues = store.overview?.services.filter((row) => row.id.startsWith('queue:')) ?? [];
  if (queues.length !== 10 || queues.some((row) => typeof row.metrics[key] !== 'number'))
    return null;
  return queues.reduce((sum, row) => sum + Number(row.metrics[key]), 0);
}
function taskMetric(
  kind: 'agent' | 'creative',
  label: string,
  status: string,
  key = status,
): Metric {
  return {
    label,
    value: value(`${kind}-tasks`, key),
    target: {
      kind,
      status,
      scope: key === 'failuresLast24Hours' ? 'errors' : 'active',
    },
  };
}
const groups = computed(() => [
  {
    label: '协作连接',
    items: [
      { label: '连接数', value: value('collaboration', 'connections') },
      { label: '在线用户', value: value('collaboration', 'users') },
      { label: '网页', value: value('collaboration', 'web') },
      { label: '桌面', value: value('collaboration', 'desktop') },
      { label: '移动端', value: value('collaboration', 'mobile') },
    ] as Metric[],
  },
  {
    label: '桌面运行',
    items: [
      { label: '连接数', value: value('desktop', 'connections') },
      { label: '在线设备', value: value('desktop', 'devices') },
      { label: '在线用户', value: value('desktop', 'users') },
    ] as Metric[],
  },
  {
    label: 'Agent 任务',
    items: [
      taskMetric('agent', '排队中', 'queued'),
      taskMetric('agent', '执行中', 'running'),
      taskMetric('agent', '等待中', 'waiting'),
      taskMetric('agent', '取消中', 'cancelling'),
      taskMetric('agent', '24 小时异常', 'all', 'failuresLast24Hours'),
    ],
  },
  {
    label: '创意任务',
    items: [
      taskMetric('creative', '排队中', 'queued'),
      taskMetric('creative', '提交中', 'submitting'),
      taskMetric('creative', '生成中', 'processing'),
      taskMetric('creative', '下载中', 'downloading'),
      taskMetric('creative', '24 小时失败', 'all', 'failuresLast24Hours'),
    ],
  },
  {
    label: '异步队列',
    items: [
      { label: '待执行 Job', value: queueTotal('waiting') },
      { label: '执行中 Job', value: queueTotal('active') },
      { label: '延迟 Job', value: queueTotal('delayed') },
    ] as Metric[],
  },
]);
function showTasks(target: Target): void {
  store.openTaskStatus(target.kind, target.status, target.scope);
  const heading = document.getElementById('task-heading');
  heading?.scrollIntoView({ block: 'start' });
  heading?.focus({ preventScroll: true });
}
</script>
<style scoped>
.metric-row {
  display: grid;
  grid-template-columns: 120px minmax(0, 1fr);
  align-items: start;
  gap: 16px;
  padding: 12px 0;
  border-top: 1px solid var(--aw-border);
}
.metric-row h3 {
  margin: 4px 0 0;
  font-size: 13px;
  font-weight: 600;
}
.metric-row dl {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 24px;
  margin: 0;
}
.metric-row dl > div {
  min-width: 90px;
}
.metric-row dt {
  color: var(--aw-text-secondary);
  font-size: 12px;
}
.metric-row dd {
  margin: 3px 0 0;
  font-size: 16px;
  font-variant-numeric: tabular-nums;
}
.metric-action {
  min-height: 28px;
  min-width: 28px;
}
@media (max-width: 767px) {
  .metric-row {
    grid-template-columns: 1fr;
    gap: 10px;
  }
  .metric-row dl {
    gap: 8px 16px;
  }
  .metric-row dl > div {
    min-width: 74px;
  }
  .metric-action {
    min-height: 44px;
    min-width: 44px;
    text-align: left;
  }
}
</style>
